import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { Fragment } from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import Box from 'src/components/Box/Box'
import InfinitySymbol from 'src/components/BudgetSummaryBox/InfinitySymbol'
import SvgRecurring from 'src/components/Svgs/SvgRecurring'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { SupportedBrands } from 'src/enums'
import Crncy from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'
import { CreditCardListModel } from 'src/models/creditCard.models'
import { budgetOverviewUrl } from 'src/navigation/urls'
import { selectBrandName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface BudgetSummaryBoxProps {
  className?: string
  creditCard: CreditCardListModel
}

const BudgetSummaryBox: React.FC<BudgetSummaryBoxProps> = (
  props,
): React.ReactElement => {
  const translate = useTranslate()

  const brand = useSelector(selectBrandName)
  const isJPM = brand === SupportedBrands.Jpm

  const backgroundGradient = isJPM
    ? css`linear-gradient(45deg, #101820 60%, #1A7B99 160%);`
    : css`linear-gradient(45deg, #1F2B4D 60%, #5687F1 160%);`
  const getProgressBarGradient = (brand: string): string => {
    switch (brand) {
      case SupportedBrands.PacWest:
        return `linear-gradient(90deg, ${themeConstants.colors.highlight} 0%, ${themeConstants.colors.highlight} 100%);`
      case SupportedBrands.Jpm:
        return `linear-gradient(90deg, #936846 0%, #4E2A08 100%);`
      default:
        return `linear-gradient(90deg, #5FCEB6 0%, #588AF2 100%);`
    }
  }
  const progressBarGradient = css`
    ${getProgressBarGradient(brand)}
  `
  const unlimitedGradient = isJPM
    ? css`linear-gradient(90deg, #936846, #4E2A08, #936846, #4E2A08)`
    : css`linear-gradient(90deg, #638aea, #7ccab8, #638aea, #7ccab8)`
  const tooltipColor =
    brand === SupportedBrands.PacWest || brand === SupportedBrands.Jpm
      ? '#ffffff'
      : undefined
  const { analytics } = useAnalyticsContext()

  const recurrenceDate =
    props.creditCard.recurrence?.nextRecurrenceAtTimezonedMoment(
      props.creditCard.timezone,
    )
  const recurrenceLabel =
    props.creditCard.recurs &&
    props.creditCard.recurrence !== undefined &&
    recurrenceDate !== undefined ? (
      <span
        css={css`
          display: flex;
          align-items: flex-end;
        `}
      >
        <SvgRecurring
          fill={
            brand === SupportedBrands.PacWest
              ? '#ffffff'
              : 'var(--color-highlight)'
          }
          style={{
            transform: 'scale(calc(22/24))',
            transformOrigin: 'bottom',
            flexShrink: 0,
          }}
          css={css`
            margin-right: 6px;
          `}
        />
        <span
          css={css`
            ${getFontCss(
              14,
              brand === SupportedBrands.PacWest
                ? '#ffffff'
                : themeConstants.colors.highlight,
              styleConstants.fontFamilies.regular,
            )}
            padding: 2px 0;
          `}
        >
          {translate('Resets on ${date}', {
            date: recurrenceDate.format('MMM D'),
          })}
        </span>
      </span>
    ) : undefined

  return (
    <Box
      noPadding
      css={css`
        height: 100%;
      `}
    >
      <div
        css={css`
          height: 100%;
          display: flex;
          flex-direction: column;
          border-top-left-radius: 10px;
          border-top-right-radius: 10px;
          border-bottom-left-radius: 10px;
          border-bottom-right-radius: 10px;
          background-image: ${backgroundGradient};
          padding: 40px 40px 38px;
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
          `}
        >
          <span
            css={css`
              ${getFontCss(
                20,
                styleConstants.colors.white,
                styleConstants.fontFamilies.regular,
              )}
              font-weight: bold;
            `}
          >
            {translate('Budget Summary')}
          </span>
          {recurrenceLabel}
        </div>
        <div
          css={css`
            margin-top: 30px;
            display: flex;
            flex-direction: column;
          `}
        >
          {/* limited budget */}
          {props.creditCard.isLimitedBudget &&
            props.creditCard.remainingAmountCents !== undefined &&
            props.creditCard.issuingLimitCents && (
              <>
                <div>
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        styleConstants.colors.white,
                        styleConstants.fontFamilies.regular,
                      )}
                      margin-bottom: 10px;
                    `}
                  >
                    {translate('Remaining')}
                  </span>
                  <div
                    css={css`
                      height: auto;
                      display: inline-block;
                      vertical-align: middle;
                    `}
                    onMouseEnter={(): void => {
                      analytics?.track(
                        'budget detail overview - view remaining tooltip - button press',
                        {
                          intent: 'view remaining amount tooltip',
                          interaction: 'button press',
                          screen: budgetOverviewUrl(':id'),
                        },
                      )
                    }}
                  >
                    <Tooltip
                      tooltipColor={tooltipColor}
                      openLeft={false}
                      css={css`
                        height: auto;
                        display: inline-block;
                        vertical-align: middle;
                      `}
                    >
                      <p>
                        {translate(
                          '${remaining} is the amount that is available to be assigned to virtual cards within this budget',
                          {
                            remaining: (
                              <span
                                css={css`
                                  font-weight: bold;
                                `}
                              >
                                {translate('Remaining')}
                              </span>
                            ),
                          },
                        )}
                      </p>
                    </Tooltip>
                  </div>
                </div>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <span
                    css={css`
                      ${getFontCss(
                        32,
                        themeConstants.colors.highlight,
                        styleConstants.fontFamilies.regular,
                      )};
                      flex-direction: row;
                    `}
                  >
                    <Crncy
                      currency={props.creditCard.currency}
                      amount={
                        Math.round(props.creditCard.remainingAmountCents) / 100
                      }
                    />
                  </span>
                  <span
                    css={css`
                      ${getFontCss(
                        16,
                        styleConstants.colors.white,
                        styleConstants.fontFamilies.regular,
                      )};
                      flex-direction: row;
                    `}
                  >
                    {` / `}
                    <Crncy
                      currency={props.creditCard.currency}
                      amount={
                        Math.round(props.creditCard.issuingLimitCents) / 100
                      }
                    />
                  </span>
                </div>
              </>
            )}
          {/* unlimited budget */}
          {!props.creditCard.isLimitedBudget && (
            <Fragment>
              {brand === SupportedBrands.PacWest ? (
                <div
                  css={css`
                    padding-top: 15px;
                  `}
                >
                  <InfinitySymbol />
                  <span
                    css={css`
                      padding-left: 1%;
                      font-size: 36px;
                      color: #ffffff;
                    `}
                  >
                    {translate('Unlimited')}
                  </span>
                </div>
              ) : (
                <div
                  css={css`
                    padding-top: 15px;
                    background: ${unlimitedGradient};
                    background-size: 300% 100%;
                    animation: gradient 5.5s ease-in infinite;

                    background-clip: text;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    @keyframes gradient {
                      0% {
                        background-position: 100% 0%;
                      }
                      50% {
                        background-position: 0% 0%;
                      }
                      100% {
                        background-position: 100% 0%;
                      }
                    }
                  `}
                >
                  <InfinitySymbol />
                  <span
                    css={css`
                      padding-left: 1%;
                      font-size: 36px;
                    `}
                  >
                    {translate('Unlimited')}
                  </span>
                </div>
              )}
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  justify-content: space-between;
                  margin-top: 20px;
                `}
              >
                <span
                  css={css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.white,
                      styleConstants.fontFamilies.regular,
                    )};
                    line-height: 128.6%;
                  `}
                >
                  {translate(
                    'This budget has no assigned limit. You can create unlimited Virtual Cards.',
                  )}
                </span>
              </div>
            </Fragment>
          )}
        </div>
        {props.creditCard.isLimitedBudget &&
          props.creditCard.issuingLimitCents !== undefined && (
            <div
              css={css`
                height: 15px;
                width: 100%;
                background-color: #f6f7fb;
                display: flex;
                flex-direction: column;
                justify-content: space-between;
                margin-top: 20px;
                align-items: center;
                height: 15px;
                border-radius: 10px;
                overflow: hidden;
              `}
            >
              <div
                css={css`
                  height: 15px;
                  width: ${props.creditCard.issuingLimitCents > 0
                    ? `${((props.creditCard.issuingLimitCents - props.creditCard.issuedAmountCents) * 100) / props.creditCard.issuingLimitCents}%`
                    : `0%`};
                  background-image: ${css`
                    ${progressBarGradient}
                  `};
                  background-color: ${props.creditCard.isInactive ||
                  props.creditCard.isClosed
                    ? `#b3b7be`
                    : null};
                  border-radius: 10px;
                  align-self: flex-start;
                `}
              />
            </div>
          )}
      </div>
    </Box>
  )
}

export default BudgetSummaryBox
